import { convertToEur, type ExchangeRates } from "./money"
import type { MarketplaceSummary } from "../services/use-dashboard"

/**
 * The marketplace switchers' order: biggest channel first, by GMV converted to
 * EUR.
 *
 * Converted for the same reason the Payouts page converts (`byLifetimePaid`):
 * the fleet sells in EUR, CZK and PLN, so a raw amount would rank a PLN channel
 * above a larger EUR one on the strength of its exchange rate alone.
 *
 * The GMV is the summary read's own window — the dashboard default, a rolling
 * 30 days. That window MOVES, so unlike the Payouts order this one is not
 * frozen: a channel that has a big month can climb. It is stable enough to find
 * a marketplace twice, because relative fleet sizes do not flip day to day, and
 * it is the only revenue figure available without fetching a statement per
 * marketplace just to sort a dropdown.
 *
 * A marketplace with no currency, or one whose currency has no known rate,
 * cannot be compared on money at all. Those sort **last, by name**, rather than
 * being silently treated as zero and mixed in among genuinely small channels.
 * Ties break by name too, so the order is total and stable across renders.
 */
export function byMarketplaceRevenue(
  rates: ExchangeRates | undefined
): (a: MarketplaceSummary, b: MarketplaceSummary) => number {
  const gmvEur = (summary: MarketplaceSummary): number | null => {
    if (summary.currency === null || !Number.isFinite(summary.gmv)) {
      return null
    }
    return convertToEur(summary.gmv, summary.currency, rates)
  }
  return (a, b) => {
    const left = gmvEur(a)
    const right = gmvEur(b)
    if (left === null || right === null) {
      if (left === right) {
        return a.marketplace.localeCompare(b.marketplace)
      }
      return left === null ? 1 : -1
    }
    return right - left || a.marketplace.localeCompare(b.marketplace)
  }
}
